import {
  AnnotationHighlightFragment,
  ResourceHtmlHighlightFragment,
  ResourcePdfHighlightFragment,
} from "~/graphql/codegen/graphql";
import { IHtmlHighlight, IPdfHighlight } from "~/typings/annotate/annotation";
import {
  IHtmlHighlightInput,
  PopoverConfiguration,
} from "~/typings/annotate/annotation";
import { CustomRange } from "~/typings/annotate/utils";

type PdfHighlight = AnnotationHighlightFragment & ResourcePdfHighlightFragment;

type HtmlHighlight = AnnotationHighlightFragment &
  ResourceHtmlHighlightFragment;

type Highlight = AnnotationHighlightFragment &
  (ResourceHtmlHighlightFragment | ResourcePdfHighlightFragment);

export const mapHighlightGQLToIHighlight = (
  h: Highlight,
): IHtmlHighlight | IPdfHighlight => {
  const highlightPDF = h as PdfHighlight;
  const highlightHTML = h as HtmlHighlight;

  return h.__typename === "PDFHighlight"
    ? ({
        __typename: highlightPDF.__typename,
        id: highlightPDF.id,
        text: highlightPDF.text,
        color: highlightPDF.color,
        model: highlightPDF.model,
        prefix: highlightPDF.prefix,
        suffix: highlightPDF.suffix,
        canEdit: highlightPDF.canEdit,
        xfdfString: highlightPDF.xfdfString,
        createdAt: highlightPDF.createdAt,
        comments: highlightPDF.comments,
        privateNote: highlightPDF.privateNote,
        page: highlightPDF.page,
        profile: {
          id: highlightHTML.profile?.id,
          username: highlightHTML.profile?.username,
          name: highlightHTML.profile?.name,
          profilePictureURL: highlightHTML.profile?.profilePictureURL,
          displayName: highlightHTML.profile?.displayName,
        },
      } as IPdfHighlight)
    : ({
        __typename: highlightHTML.__typename,
        id: highlightHTML.id,
        text: highlightHTML.text,
        color: highlightHTML.color,
        model: highlightHTML.model,
        prefix: highlightHTML.prefix,
        suffix: highlightHTML.suffix,
        canEdit: highlightHTML.canEdit,
        startOffset: highlightHTML.startOffset,
        startParentID: highlightHTML.startParentID,
        endParentID: highlightHTML.endParentID,
        endOffset: highlightHTML.endOffset,
        createdAt: highlightHTML.createdAt,
        comments: highlightHTML.comments,
        privateNote: highlightPDF.privateNote,
        profile: {
          id: highlightHTML.profile?.id,
          username: highlightHTML.profile?.username,
          name: highlightHTML.profile?.name,
          profilePictureURL: highlightHTML.profile?.profilePictureURL,
          displayName: highlightHTML.profile?.displayName,
        },
      } as IHtmlHighlight);
};

/**
 * Build the pending ("fake") highlight for a selection, or `null` when the
 * selection's start or end has no indexed ancestor within reach — a heading,
 * an image, page chrome. `null` is the caller's cue to clear the pending
 * selection and tell the learner; it used to `throw "invalid"` here, which
 * nothing caught, so the highlight silently never appeared and Sentry logged an
 * `<unknown>` for about one reader in twelve (WEB-SPACE-H).
 */
export const getHighlightFromRange = (
  rangeForStart: CustomRange,
  rangeForEnd: CustomRange,
  popoverConfiguration: PopoverConfiguration,
  text: string,
  /** The annotator's content root; the indexed-ancestor walk never goes past it. */
  root?: Node | null,
): IHtmlHighlightInput | null => {
  // element hierarchy is chunk > subchunk > text. Text is container returned by
  const start = getStartIDAndOffset(rangeForStart, root);
  const end = getEndIDAndOffset(rangeForEnd, root);
  if (!start || !end) return null;
  // const highlighter = getDefaultHighlighter(popoverConfiguration);

  return {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- GraphQL __typename
    __typename: "HTMLHighlight",
    id: "temp",
    startParentID: Number(start.id),
    endParentID: Number(end.id),
    startOffset: start.offset,
    endOffset: end.offset,
    model: "marker0",
    color: "#ffffff",
    prefix: start.parentWithIndex?.textContent?.substring(0, start.offset) || "",
    suffix: end.parentWithIndex?.textContent?.substring(end.offset) || "",
    text,
  };
};

type IndexedAnchor = { id: string; offset: number; parentWithIndex: Node };

/**
 * How far up from a selection edge we look for the indexed chunk when no root
 * is given. Article markup is chunk > inline wrappers (highlight spans with
 * `data-offset`, <strong>, <em>, <a>, <code>, <mark>…) > text; the root bound
 * is the real fence, this is only a backstop against walking a whole page.
 */
const MAX_ANCESTOR_DEPTH = 12;

const asElement = (node: Node | null | undefined): HTMLElement | null =>
  node && node.nodeType === 1 ? (node as HTMLElement) : null;

/**
 * Nearest ancestor-or-self carrying `data-index`, accumulating the
 * `data-offset` of every wrapper passed on the way (a highlight span's offset
 * is relative to its chunk, so nested wrappers add up). `null` when the walk
 * runs out of depth without finding a chunk.
 */
const findIndexedAncestor = (
  from: Node | null | undefined,
  baseOffset: number,
  root: Node | null | undefined,
): IndexedAnchor | null => {
  let node: HTMLElement | null = asElement(from);
  let offset = baseOffset;
  for (let depth = 0; node && depth < MAX_ANCESTOR_DEPTH; depth += 1) {
    if (root && node === root) return null; // the root itself is never a chunk
    if (node.dataset?.index) {
      return { id: node.dataset.index, offset, parentWithIndex: node };
    }
    if (node.dataset?.offset) {
      offset += Number(node.dataset.offset);
    }
    node = node.parentElement;
  }
  return null;
};

/** The first indexed element inside a container (offset 0 by construction). */
const firstIndexedDescendant = (container: Node): HTMLElement | null => {
  const element = asElement(container);
  if (!element) return null;
  return element.querySelector<HTMLElement>("[data-index]");
};

const getStartIDAndOffset = (range: CustomRange, root?: Node | null): IndexedAnchor | null => {
  const container = range.startContainer;
  const parent = asElement(container.parentElement);
  if (!parent?.dataset?.index) {
    // Highlighting "in space": the selection starts just before a chunk.
    const next = asElement(container.nextSibling);
    if (next?.dataset?.index) {
      return { id: next.dataset.index, offset: 0, parentWithIndex: next };
    }
  }
  return findIndexedAncestor(parent, range.startOffset, root);
};

const getEndIDAndOffset = (range: CustomRange, root?: Node | null): IndexedAnchor | null => {
  const container = range.endContainer;
  // If you stop just "before" a chunk, the endContainer is the new chunk's
  // parent rather than the last character of the previous one: the first
  // indexed element inside it (a direct child, one wrapped in <strong> or
  // <blockquote>, or — in a video transcript — the second span after the
  // timestamp span) carries the index, and there can be no offset then.
  const firstIndexed = asElement(container.parentElement)?.dataset?.index
    ? null
    : firstIndexedDescendant(container);
  if (firstIndexed?.dataset?.index) {
    return { id: firstIndexed.dataset.index, offset: 0, parentWithIndex: firstIndexed };
  }

  const parent = asElement(container.parentElement);
  if (!parent?.dataset?.index) {
    const previous = asElement(container.previousSibling);
    if (previous?.dataset?.index) {
      return { id: previous.dataset.index, offset: 0, parentWithIndex: previous };
    }
  }
  return findIndexedAncestor(parent, range.endOffset, root);
};
